export type Locale = "en" | "zh";

export type LocalizedText = Record<Locale, string>;

export type NavItem = {
  id: string;
  label: LocalizedText;
};

export type Feature = {
  icon: "wand" | "send" | "coin" | "chart";
  title: LocalizedText;
  text: LocalizedText;
};

export type Persona = {
  icon: "creators" | "brands" | "operators" | "developers";
  title: LocalizedText;
  text: LocalizedText;
  highlights: Record<Locale, string[]>;
};

export type Proof = {
  icon: "shield" | "bolt" | "heart" | "lock";
  title: LocalizedText;
  text: LocalizedText;
};

export const navItems: NavItem[] = [
  { id: "platform", label: { en: "Capabilities", zh: "核心能力" } },
  { id: "creators", label: { en: "Workflows", zh: "工作流" } },
  { id: "brands", label: { en: "Monetization", zh: "变现闭环" } },
  { id: "developers", label: { en: "Agents", zh: "Agent 接入" } }
];

export const features: Feature[] = [
  {
    icon: "wand",
    title: { en: "Create with Agents", zh: "Agent 创作" },
    text: {
      en: "Turn a brief into copy, images, videos, and draft batches.",
      zh: "一句话生成文案、图片、视频和批量草稿。"
    }
  },
  {
    icon: "send",
    title: { en: "Publish to 15+ channels", zh: "发布到 15+ 平台" },
    text: {
      en: "Schedule and distribute to TikTok, YouTube, Instagram, X, Rednote, and more.",
      zh: "排期并分发到抖音、小红书、TikTok、YouTube、Instagram、X 等平台。"
    }
  },
  {
    icon: "coin",
    title: { en: "Monetize results", zh: "按结果变现" },
    text: {
      en: "Run bounty tasks, tips, referrals, CPS/CPE/CPM campaigns, and payouts.",
      zh: "支持悬赏、打赏、推荐返佣、CPS/CPE/CPM 活动和结算。"
    }
  },
  {
    icon: "chart",
    title: { en: "Engage and measure", zh: "互动与分析" },
    text: {
      en: "Track growth, engagement, brand signals, revenue, and campaign ROI.",
      zh: "追踪增长、互动、品牌信号、收入和活动 ROI。"
    }
  }
];

export const personas: Persona[] = [
  {
    icon: "creators",
    title: { en: "Creators and OPCs", zh: "创作者与一人公司" },
    text: {
      en: "Use Agents to plan content, generate finished drafts, publish everywhere, and earn from bounties, tips, and referrals.",
      zh: "用 Agent 规划内容、生成成品草稿、多平台发布，并通过悬赏、打赏和推荐返佣获得收入。"
    },
    highlights: {
      en: ["Draft batches", "15+ channels", "Bounties & tips"],
      zh: ["批量草稿", "15+ 平台", "悬赏与打赏"]
    }
  },
  {
    icon: "brands",
    title: { en: "Brands", zh: "品牌方" },
    text: {
      en: "Launch result-based campaigns, review creator submissions, and connect spend to views, engagement, and sales.",
      zh: "发起按结果结算的推广活动，审核创作者投稿，并把预算和播放、互动、成交结果连接起来。"
    },
    highlights: {
      en: ["CPS/CPE/CPM", "Submission review", "ROI tracking"],
      zh: ["CPS/CPE/CPM", "投稿审核", "ROI 追踪"]
    }
  },
  {
    icon: "operators",
    title: { en: "Growth operators", zh: "增长运营团队" },
    text: {
      en: "Coordinate accounts, calendars, engagement queues, wallet ledgers, and campaign dashboards from one operating desk.",
      zh: "在一个运营台管理账号、排期、互动队列、钱包流水和活动数据看板。"
    },
    highlights: {
      en: ["Account matrix", "Engagement queue", "Wallet ledger"],
      zh: ["账号矩阵", "互动队列", "钱包流水"]
    }
  },
  {
    icon: "developers",
    title: { en: "Developers", zh: "开发者" },
    text: {
      en: "Connect AiToEarn to Claude, Cursor, OpenClaw, and custom agents with MCP, SSE, Relay, and APIs.",
      zh: "通过 MCP、SSE、Relay 和 API，把 AiToEarn 接入 Claude、Cursor、OpenClaw 和自定义 Agent。"
    },
    highlights: {
      en: ["MCP & SSE", "Relay auth", "OpenClaw tasks"],
      zh: ["MCP 与 SSE", "Relay 授权", "OpenClaw 任务"]
    }
  }
];

export const monetizationCards = [
  {
    title: { en: "Result-based bounties", zh: "按结果结算的悬赏" },
    text: {
      en: "Brands publish tasks, creators submit proof, and approved CPS/CPE/CPM work settles through the wallet.",
      zh: "品牌方发布任务，创作者提交结果，CPS/CPE/CPM 审核通过后进入钱包结算。"
    },
    value: "$320",
    label: { en: "sample reward", zh: "示例奖励" }
  },
  {
    title: { en: "Wallet-grade ledger", zh: "钱包级流水" },
    text: {
      en: "Top-ups, subscriptions, tips, referrals, bonuses, and creator payouts stay traceable.",
      zh: "充值、订阅、打赏、推荐、奖励和创作者提现都可追踪。"
    },
    value: "$12.8k",
    label: { en: "estimated earnings", zh: "预计收入" }
  },
  {
    title: { en: "Campaign ROI cockpit", zh: "活动 ROI 驾驶舱" },
    text: {
      en: "See submissions, engagement, creator ranking, and cost per result in one view.",
      zh: "统一查看投稿、互动、创作者排名和单次结果成本。"
    },
    value: "18.6%",
    label: { en: "weekly growth", zh: "本周增长" }
  }
];

export const proofs: Proof[] = [
  {
    icon: "shield",
    title: { en: "Operator-owned channels", zh: "自有渠道控制" },
    text: { en: "Keep credentials, accounts, and data under your control.", zh: "平台凭据、账号和数据由你掌控。" }
  },
  {
    icon: "bolt",
    title: { en: "Agent workflows", zh: "Agent 工作流" },
    text: { en: "Generate, edit, schedule, publish, and follow up.", zh: "生成、编辑、排期、发布和跟进一站完成。" }
  },
  {
    icon: "heart",
    title: { en: "Creator earning loop", zh: "创作者收入闭环" },
    text: { en: "Bounties, tips, referrals, payouts, and rankings.", zh: "悬赏、打赏、推荐、提现和排名。" }
  },
  {
    icon: "lock",
    title: { en: "API-ready extension", zh: "API 化扩展" },
    text: { en: "MCP, SSE, Relay, and APIs for agent ecosystems.", zh: "MCP、SSE、Relay 与 API 接入 Agent 生态。" }
  }
];

export const copy = {
  en: {
    metaTitle: "iEarn Portal",
    metaDescription:
      "iEarn portal for AI content creation, 15+ social publishing, engagement automation, creator bounties, wallet payouts, analytics, MCP, Relay, and API access.",
    launch: "Open Studio",
    seeHow: "Explore workflows",
    language: "EN",
    heroLine1: "Create with agents.",
    heroLine2: "Publish to 15+ channels.",
    heroLine3: "Turn content into revenue.",
    heroBody:
      "AiToEarn brings AI creation, multi-platform publishing, engagement operations, creator bounties, wallet payouts, and analytics into one growth workspace.",
    calendarTitle: "Publishing calendar",
    calendarDays: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    calendarCards: ["Post", "Short", "Live", "Reel", "Brief"],
    earningsTitle: "Creator earnings",
    earningsGrowth: "18.6% this week",
    bountyTitle: "Active bounty",
    bountyName: "Launch review video",
    bountyReward: "Payout",
    featureLabel: "Full-funnel creator growth capabilities",
    universeTitle: "One workspace for every role in the creator growth loop",
    developerTitle: "Agent, MCP, Relay, and API access",
    developerBody:
      "Use AiToEarn from MCP-compatible tools such as Claude and Cursor, run earning tasks in OpenClaw, or connect custom agents through SSE, Relay, and platform APIs.",
    productEntrance: "Growth workspace",
    entranceBody: "Create, publish, engage, monetize, and measure from one place.",
    monetizationTitle: "Monetize every content loop",
    monetizationBody:
      "Turn content distribution into measurable revenue with result-based bounties, wallet ledgers, tips, referrals, creator rankings, campaign ROI, and payout-ready operations.",
    flowLabels: ["Brand brief", "Bounty", "Creator proof", "Payout"],
    footerCopyright: "Copyright © 2026 AICOFI PTE. LTD. All rights reserved.",
    footerLegal: "Legal",
    footerSocial: "Social media",
    footerPrivacy: "Privacy",
    footerTerms: "Terms",
    footerContact: "Contact",
    skipToContent: "Skip to main content",
    switchLanguage: "切换为中文",
    switchToLight: "Switch to light mode",
    switchToDark: "Switch to dark mode",
    aicofiHome: "AICOFI home",
    authCompleting: "Completing sign-in…"
  },
  zh: {
    metaTitle: "iEarn 门户",
    metaDescription:
      "iEarn 门户网站，展示 AI 内容创作、15+ 社媒发布、互动运营、创作者悬赏、钱包结算、数据分析、MCP、Relay 与 API 接入能力。",
    launch: "打开工作台",
    seeHow: "查看工作流",
    language: "中文",
    heroLine1: "Agent 创作。",
    heroLine2: "分发 15+ 平台。",
    heroLine3: "内容变收入。",
    heroBody:
      "AiToEarn 把 AI 创作、多平台发布、互动运营、创作者悬赏、钱包结算和数据分析放进同一个增长工作台。",
    calendarTitle: "发布排期",
    calendarDays: ["周一", "周二", "周三", "周四", "周五"],
    calendarCards: ["图文", "短视频", "直播", "Reel", "简报"],
    earningsTitle: "创作者收入",
    earningsGrowth: "本周增长 18.6%",
    bountyTitle: "进行中的悬赏",
    bountyName: "新品测评视频",
    bountyReward: "结算",
    featureLabel: "覆盖创作者增长全链路的能力",
    universeTitle: "一个工作台，连接创作者增长闭环里的每一种角色",
    developerTitle: "Agent、MCP、Relay 与 API 接入",
    developerBody:
      "可在 Claude、Cursor 等 MCP 工具中使用 AiToEarn，也可在 OpenClaw 里执行收益任务，或通过 SSE、Relay 和平台 API 接入自定义 Agent。",
    productEntrance: "增长工作台",
    entranceBody: "创作、发布、互动、变现和分析，都从这里开始。",
    monetizationTitle: "把每一次创作循环变成收入",
    monetizationBody:
      "通过按结果结算的悬赏任务、钱包流水、打赏、推荐返佣、创作者排名、活动 ROI 和可提现运营，把内容分发转化为可衡量收入。",
    flowLabels: ["品牌 Brief", "悬赏任务", "创作者提交", "钱包结算"],
    footerCopyright: "版权所有 © 2026 AICOFI PTE. LTD. 保留所有权利。",
    footerLegal: "法律",
    footerSocial: "社交媒体",
    footerPrivacy: "隐私政策",
    footerTerms: "服务条款",
    footerContact: "联系我们",
    skipToContent: "跳转到主要内容",
    switchLanguage: "Switch to English",
    switchToLight: "切换为浅色模式",
    switchToDark: "切换为深色模式",
    aicofiHome: "AICOFI 首页",
    authCompleting: "正在完成登录…"
  }
} satisfies Record<Locale, Record<string, string | string[]>>;
